import { useState } from 'react';
import { cn } from '../lib/cn';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

const sizes = {
  sm: 'size-8 text-xs',
  md: 'size-10 text-sm',
  lg: 'size-12 text-base',
};

export interface AvatarProps {
  name: string;
  size?: keyof typeof sizes;
  className?: string;
}

/** A fun-emoji avatar (DiceBear, seeded by name) that falls back to the
 * member's initials on a deterministic tint if the image fails to load. */
export function Avatar({ name, size = 'md', className }: AvatarProps) {
  const [failed, setFailed] = useState(false);
  // Deterministic tint so the same member always reads the same colour.
  const tints = ['bg-ok-soft text-ok-ink', 'bg-warn-soft text-warn-ink', 'bg-accent-soft text-ink', 'bg-neutral-soft text-neutral-ink'];
  const tint = tints[name.length % tints.length];
  const showImage = !failed && name.trim().length > 0;

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full font-medium',
        sizes[size],
        !showImage && tint,
        className,
      )}
      aria-hidden
    >
      {showImage ? (
        <img
          src={`https://api.dicebear.com/9.x/fun-emoji/svg?seed=${encodeURIComponent(name.trim())}`}
          alt=""
          loading="lazy"
          className="size-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        initials(name)
      )}
    </span>
  );
}
